import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import Pagination from '../../components/Pagination'
import InvoiceModal from '../../components/finance/InvoiceModal'
import { formatUGX } from '../../components/finance/documentTokens'
import { DollarSign, Calendar, CreditCard, CheckCircle, AlertCircle, Clock, X, Copy, Download, ChevronLeft, ChevronRight, Upload, FileText, CheckCircle2, XCircle, Receipt as ReceiptIcon } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

const ITEMS_PER_PAGE = 10

export default function Payments({ context = 'student' }) {
  const [invoices, setInvoices] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [payingInvoice, setPayingInvoice] = useState(null)
  const [previewInvoice, setPreviewInvoice] = useState(null)
  const [studentProfile, setStudentProfile] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submissionForm, setSubmissionForm] = useState({
    amount: '',
    payment_method: 'mobile_money',
    transaction_reference: '',
    notes: '',
    proof_file: null
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [invoicesRes, profileRes, submissionsRes, receiptsRes] = await Promise.all([
        api.get('/students/invoices'),
        api.get('/students/profile'),
        api.get('/finance/payment-submissions/my-submissions').catch(() => ({ data: { submissions: [] } })),
        api.get('/finance/receipts?limit=50').catch(() => ({ data: { receipts: [] } }))
      ])
      setInvoices(invoicesRes.data || [])
      setStudentProfile(profileRes.data || null)
      setSubmissions(submissionsRes.data?.submissions || [])
      setReceipts(receiptsRes.data?.receipts || [])
    } catch (error) {
      toast.error('Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }

  const openPayModal = (invoice) => {
    if (isIncludedInvoice(invoice)) return
    const balance = parseFloat(invoice.amount) - parseFloat(invoice.amount_paid || 0)
    setPayingInvoice(invoice)
    setSubmissionForm({
      amount: balance > 0 ? balance : '',
      payment_method: 'mobile_money',
      transaction_reference: '',
      notes: '',
      proof_file: null
    })
  }

  const handlePaymentSubmit = async (e) => {
    e.preventDefault()
    if (!payingInvoice) return
    const numAmount = parseFloat(submissionForm.amount)
    if (isNaN(numAmount) || numAmount <= 0) {
      return toast.error('Please enter a valid payment amount')
    }
    const balance = parseFloat(payingInvoice.amount) - parseFloat(payingInvoice.amount_paid || 0)
    if (numAmount > balance) {
      return toast.error(`Amount cannot exceed the balance due (${formatCurrency(balance)})`)
    }
    if (!submissionForm.transaction_reference.trim()) {
      return toast.error('Please enter the transaction reference / Mobile Money ID')
    }

    setSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('invoice_id', payingInvoice.id)
      formData.append('amount', numAmount)
      formData.append('payment_method', submissionForm.payment_method)
      formData.append('transaction_reference', submissionForm.transaction_reference.trim())
      if (submissionForm.notes) formData.append('notes', submissionForm.notes.trim())
      if (submissionForm.proof_file) formData.append('proof_file', submissionForm.proof_file)

      await api.post('/finance/payment-submissions', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      toast.success('Payment submitted! SMA Finance will verify your payment.')
      setPayingInvoice(null)
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit payment')
    } finally {
      setSubmitting(false)
    }
  }

  const filteredInvoices = invoices.filter(invoice => {
    if (filter === 'pending') return invoice.status === 'pending'
    if (filter === 'partial') return invoice.status === 'partial'
    if (filter === 'paid') return invoice.status === 'paid'
    if (filter === 'overdue') return invoice.status === 'overdue'
    return true
  })

  const totalPages = Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE)
  const currentInvoices = filteredInvoices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  // Authoritative UGX presentation — Intl 'en-UG' renders "USh" in some
  // environments; SMA finance documents standardize on the literal "UGX".
  const formatCurrency = formatUGX

  // Zero-value Project Lab invoices are legitimate commercial records
  // (project configured with no participant-facing fee). They must never
  // read as "you owe money": no payment CTA, and the status presents as
  // "Included". The invoice row itself is preserved untouched.
  const isIncludedInvoice = (invoice) =>
    invoice.payment_purpose === 'PROJECT_LAB' && parseFloat(invoice.amount || 0) <= 0

  // Check if an invoice has a payment submission that is still pending verification.
  // Used to show a "Pending Verification" badge so students know their submission
  // is under review — and that any visible receipt is from a *previous* approved
  // payment, not the submission they just sent.
  const pendingSubmissionForInvoice = (invoiceId) =>
    submissions.some(s => s.invoice_id === invoiceId && s.status === 'PENDING_VERIFICATION')

  const totalOwed = invoices
    .filter(i => i.status !== 'paid')
    .reduce((sum, i) => sum + (parseFloat(i.amount) - parseFloat(i.amount_paid || 0)), 0)

  const totalPaid = invoices
    .reduce((sum, i) => sum + parseFloat(i.amount_paid || 0), 0)

  // Open the invoice preview modal — PDF/PNG/Print all export the exact same
  // DOM shown in the preview, so the download can never drift from the preview.
  const handlePreviewInvoice = (invoice) => {
    setPreviewInvoice(invoice)
  }

  const statusCls = {
    paid:    'bg-green-500/10 text-green-400 border-green-500/25',
    pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/25',
    partial: 'bg-blue-500/10 text-blue-400 border-blue-500/25',
    overdue: 'bg-red-500/10 text-red-400 border-red-500/25',
  }

  const subStatusCls = {
    APPROVED: 'bg-green-500/10 text-green-400 border-green-500/25',
    PENDING_VERIFICATION: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/25',
    REJECTED: 'bg-red-500/10 text-red-400 border-red-500/25',
    CANCELLED: 'bg-gray-500/10 text-gray-400 border-gray-500/25'
  }

  if (loading) return <LoadingSpinner label="Loading invoices" />

  return (
    <div className="space-y-10">
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-1">{context === 'project-lab' ? 'Project Lab' : 'Student Portal'}</span>
          <h1 className="font-display text-2xl text-white font-light">Payments &amp; Invoices</h1>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8">
        <div className="cinema-stat">
          <span className="cinema-stat-label">Amount Owed</span>
          <span className="cinema-stat-value text-red-400">{formatCurrency(totalOwed)}</span>
        </div>
        <div className="cinema-stat">
          <span className="cinema-stat-label">Total Paid</span>
          <span className="cinema-stat-value text-green-400">{formatCurrency(totalPaid)}</span>
        </div>
        <div className="cinema-stat">
          <span className="cinema-stat-label">Total Invoices</span>
          <span className="cinema-stat-value text-cinema-glow">{invoices.length}</span>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {[{ key: 'all', label: 'All Invoices' }, { key: 'pending', label: 'Pending' }, { key: 'partial', label: 'Partial' }, { key: 'paid', label: 'Paid' }, { key: 'overdue', label: 'Overdue' }].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setFilter(key); setCurrentPage(1) }}
            className={`px-4 py-1.5 text-xs font-sans transition-all border ${
              filter === key
                ? 'bg-cinema-glow/10 text-cinema-glow border-cinema-glow/30'
                : 'cinema-glass border-white/8 text-cinema-muted hover:text-white hover:border-white/20'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {filteredInvoices.length === 0 ? (
        <div className="cinema-empty">
          <CreditCard size={36} className="mx-auto mb-4 text-cinema-muted/20" />
          <p className="font-sans text-sm text-cinema-muted">No invoices found</p>
        </div>
      ) : (
        <div className="pt-8 border-t border-white/5">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/6">
                  {['Invoice #', 'Course', 'Issued', 'Due', 'Amount', 'Paid', 'Status', ''].map((h) => (
                    <th key={h} className={`py-3 px-2 cinema-label font-normal ${h === 'Amount' || h === 'Paid' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-white/4 hover:bg-white/[0.02] transition-colors">
                    <td className="py-4 px-2 font-mono text-xs sm:text-sm text-cinema-glow font-medium">#{invoice.invoice_number}</td>
                    <td className="py-4 px-2 font-sans text-xs sm:text-sm text-white font-medium max-w-[160px] truncate">{invoice.course_title || invoice.description || '—'}</td>
                    <td className="py-4 px-2 font-sans text-xs sm:text-sm text-cinema-muted">{invoice.issued_date ? new Date(invoice.issued_date).toLocaleDateString() : '—'}</td>
                    <td className="py-4 px-2 font-sans text-xs sm:text-sm text-cinema-muted">{new Date(invoice.due_date).toLocaleDateString()}</td>
                    <td className="py-4 px-2 font-sans text-xs sm:text-sm text-white font-medium text-right">{formatCurrency(invoice.amount)}</td>
                    <td className="py-4 px-2 font-sans text-xs sm:text-sm text-green-400 font-medium text-right">{isIncludedInvoice(invoice) ? '—' : formatCurrency(invoice.amount_paid || 0)}</td>
                    <td className="py-4 px-2">
                      {isIncludedInvoice(invoice) ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm bg-white/5 text-cinema-soft border-white/15">
                          Included
                        </span>
                      ) : (
                        <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${statusCls[invoice.status] || statusCls.pending}`}>
                          {invoice.status}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-2">
                      <div className="flex items-center gap-3 justify-end">
                        {invoice.status !== 'paid' && !isIncludedInvoice(invoice) && !pendingSubmissionForInvoice(invoice.id) && (
                          <button onClick={() => openPayModal(invoice)} className="font-sans text-xs sm:text-sm text-cinema-glow hover:text-cinema-amber transition-colors font-medium">
                            Submit Payment
                          </button>
                        )}
                        {pendingSubmissionForInvoice(invoice.id) && (
                          <span className="inline-flex items-center gap-1 font-sans text-xs text-yellow-400/90">
                            <Clock size={13} /> Pending Verification
                          </span>
                        )}
                        {receipts.filter(r => r.invoice_number === invoice.invoice_number).map(r => (
                          <Link
                            key={r.payment_id}
                            to={`/student/receipts/${r.payment_id}`}
                            className="inline-flex items-center gap-1 font-sans text-xs sm:text-sm text-green-400 hover:text-green-300 transition-colors font-medium"
                            title={`Receipt ${r.receipt_number} — available after payment approval`}
                          >
                            <ReceiptIcon size={14} /> {r.receipt_number}
                          </Link>
                        ))}
                        <button onClick={() => handlePreviewInvoice(invoice)} className="text-cinema-muted hover:text-white transition-colors" title="View / Download Invoice">
                          <Download size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 py-4 border-t border-white/6">
              <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="p-2 cinema-glass border border-white/10 text-cinema-muted hover:text-white disabled:opacity-30 transition-all">
                <ChevronLeft size={14} />
              </button>
              <span className="font-sans text-xs text-cinema-muted tracking-widest">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="p-2 cinema-glass border border-white/10 text-cinema-muted hover:text-white disabled:opacity-30 transition-all">
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Payment Submissions Section */}
      <div className="pt-10 border-t border-white/8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <span className="cinema-label block mb-1">Verification Queue</span>
            <h2 className="font-display text-xl text-white font-light">My Payment Submissions</h2>
          </div>
          <span className="font-mono text-xs text-cinema-muted">{submissions.length} submission{submissions.length === 1 ? '' : 's'}</span>
        </div>

        {submissions.length === 0 ? (
          <div className="cinema-glass border border-white/6 rounded-sm p-6 text-center">
            <FileText size={28} className="mx-auto mb-2 text-cinema-muted/30" />
            <p className="font-sans text-xs sm:text-sm text-cinema-muted">No payment submissions found. When you submit payment proof, it will appear here for tracking.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/6">
                  {['Submitted', 'Invoice #', 'Amount', 'Method', 'Transaction Ref', 'Status', 'Notes / Review'].map((h) => (
                    <th key={h} className={`py-3 px-2 cinema-label font-normal ${h === 'Amount' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub) => (
                  <tr key={sub.id} className="border-b border-white/4 hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 px-2 font-sans text-xs sm:text-sm text-cinema-muted">{new Date(sub.submitted_at).toLocaleDateString()}</td>
                    <td className="py-3 px-2 font-mono text-xs sm:text-sm text-cinema-glow font-medium">#{sub.invoice_number}</td>
                    <td className="py-3 px-2 font-sans text-xs sm:text-sm text-white text-right font-semibold">{formatCurrency(sub.amount)}</td>
                    <td className="py-3 px-2 font-sans text-xs sm:text-sm text-cinema-muted capitalize">{sub.payment_method?.replace('_', ' ')}</td>
                    <td className="py-3 px-2 font-mono text-xs sm:text-sm text-cinema-soft">{sub.transaction_reference}</td>
                    <td className="py-3 px-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${subStatusCls[sub.status] || subStatusCls.PENDING_VERIFICATION}`}>
                        {sub.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-2 font-sans text-xs sm:text-sm text-cinema-muted max-w-[200px] truncate">
                      {sub.review_notes ? (
                        <span className={sub.status === 'REJECTED' ? 'text-red-400' : 'text-cinema-soft'}>{sub.review_notes}</span>
                      ) : (
                        sub.notes || '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pay Now / Submit Payment Modal */}
      {payingInvoice && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="cinema-glass-strong border border-white/10 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <div>
                <h2 className="font-display text-lg text-white font-light">Submit Payment Proof</h2>
                <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">Invoice #{payingInvoice.invoice_number} · {payingInvoice.course_title}</p>
              </div>
              <button onClick={() => setPayingInvoice(null)} className="text-cinema-muted hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Invoice summary */}
              <div className="cinema-glass border border-white/8 rounded-sm p-4 space-y-3">
                <div className="space-y-2">
                  <div className="flex justify-between font-sans text-xs sm:text-sm">
                    <span className="text-cinema-muted">Total Amount</span>
                    <span className="text-white font-medium">{formatCurrency(payingInvoice.amount)}</span>
                  </div>
                  <div className="flex justify-between font-sans text-xs sm:text-sm">
                    <span className="text-cinema-muted">Already Paid</span>
                    <span className="text-green-400 font-medium">{formatCurrency(payingInvoice.amount_paid || 0)}</span>
                  </div>
                  <div className="flex justify-between font-sans text-xs sm:text-sm border-t border-white/8 pt-2">
                    <span className="text-cinema-muted font-medium">Balance Due</span>
                    <span className="text-red-400 font-semibold">{formatCurrency(parseFloat(payingInvoice.amount) - parseFloat(payingInvoice.amount_paid || 0))}</span>
                  </div>
                </div>
              </div>

              {/* How to pay instructions */}
              <div className="cinema-glass border border-blue-500/20 rounded-sm p-4 space-y-3">
                <p className="cinema-label">Official Payment Channels</p>
                <div className="font-sans text-xs text-cinema-soft space-y-2">
                  <div>
                    <p className="text-white font-medium">Mobile Money (Airtel / MTN)</p>
                    <p>Send to: <span className="font-mono text-cinema-glow">0763875564</span> (Kiggundu Yacob)</p>
                  </div>
                  <div className="border-t border-white/8 pt-2">
                    <p className="text-white font-medium">Bank Transfer (Standard Chartered)</p>
                    <p>Account: <span className="font-mono text-cinema-glow">0100823802202</span> (Joseph Sengendo)</p>
                  </div>
                </div>
              </div>

              {/* Submission Form */}
              <form onSubmit={handlePaymentSubmit} className="space-y-4">
                <div>
                  <label className="block font-sans text-xs text-cinema-muted mb-1">Amount Paid (UGX) *</label>
                  <input
                    type="number"
                    min="1000"
                    step="500"
                    required
                    value={submissionForm.amount}
                    onChange={(e) => setSubmissionForm({ ...submissionForm, amount: e.target.value })}
                    className="w-full bg-cinema-graphite border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-cinema-glow"
                    placeholder="e.g. 50000"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-sans text-xs text-cinema-muted mb-1">Payment Method *</label>
                    <select
                      value={submissionForm.payment_method}
                      onChange={(e) => setSubmissionForm({ ...submissionForm, payment_method: e.target.value })}
                      className="w-full bg-cinema-graphite border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-cinema-glow"
                    >
                      <option value="mobile_money">Mobile Money</option>
                      <option value="airtel_money">Airtel Money</option>
                      <option value="mtn_money">MTN Mobile Money</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash at Campus</option>
                      <option value="card">Credit / Debit Card</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-sans text-xs text-cinema-muted mb-1">Transaction Ref / ID *</label>
                    <input
                      type="text"
                      required
                      value={submissionForm.transaction_reference}
                      onChange={(e) => setSubmissionForm({ ...submissionForm, transaction_reference: e.target.value })}
                      className="w-full bg-cinema-graphite border border-white/10 px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-cinema-glow"
                      placeholder="e.g. MP260819.1234"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-xs text-cinema-muted mb-1">Upload Receipt / Screenshot (Optional)</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={(e) => setSubmissionForm({ ...submissionForm, proof_file: e.target.files[0] || null })}
                    className="w-full bg-cinema-graphite border border-white/10 px-3 py-2 text-xs text-cinema-muted focus:outline-none file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-cinema-glow/20 file:text-cinema-glow file:text-xs file:font-sans"
                  />
                </div>

                <div>
                  <label className="block font-sans text-xs text-cinema-muted mb-1">Additional Notes</label>
                  <textarea
                    rows={2}
                    value={submissionForm.notes}
                    onChange={(e) => setSubmissionForm({ ...submissionForm, notes: e.target.value })}
                    className="w-full bg-cinema-graphite border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-cinema-glow"
                    placeholder="Sender name, phone number, or payment details..."
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setPayingInvoice(null)}
                    className="flex-1 cinema-btn-ghost text-xs py-2.5 min-h-0"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 cinema-btn-primary text-xs py-2.5 min-h-0 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Payment Proof'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invoice preview + download (PDF / PNG / Print) — exports the exact
          same DOM shown here, so the download always matches the preview. */}
      <InvoiceModal
        invoice={previewInvoice}
        isOpen={!!previewInvoice}
        onClose={() => setPreviewInvoice(null)}
      />
    </div>
  )
}

